import { LegalReviewService } from "../../legal-reviews/legal-review.service";
import { HearingCycleContextStore } from "../../hearing-detail/hearing-cycle-context.store";
import { LegalReviewDetail } from "../../legal-reviews/legal-review.models";
import { HearingNavigationService } from "../../hearing-detail/hearing-navigation.service";
import { HearingPhaseBarDirective } from "../../hearing-detail/hearing-phase-bar.directive";
import { EzButtonComponent } from "@template-ui/components/ez-button/ez-button.component";
import { CommonModule } from "@angular/common";
import { Component, DestroyRef, Input, ViewChild, computed, effect, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { catchError, forkJoin, map, of, switchMap } from "rxjs";

import { HearingMilestoneOverview } from "@app/case-context/models/hearing-milestone-overview.model";
import { HearingMilestoneOverviewService } from "@app/case-context/services/hearing-milestone-overview.service";
import { RoleService } from "@app/shared/services/roles.service";
import { PhmsAuditActivityLogComponent } from "../../audit-activity/phms-audit-activity-log.component";
import { PhmsDocument } from "../../documents/phms-document.model";
import { PhmsDocumentService } from "../../documents/phms-document.service";
import { PhmsCaseDocumentsComponent } from "../../documents/phms-case-documents.component";
import { HearingRecordService } from "../../hearing-record/hearing-record.service";
import { HearingRecordDto } from "../../hearing-record/hearing-record.models";
import { YourDecisionComponent } from "../../hearing-record/your-decision.component";
import { LegalReviewWorkspacePanelComponent } from "../../legal-reviews/legal-review-workspace-panel.component";
import { PhmsCaseLifecycleComponent } from "../lifecycle/phms-case-lifecycle.component";
import {
  PostHearingTask,
  PostHearingCompletionSource,
  WorkflowDecisionRecord,
  formatWorkflowStatus,
  mapPostHearingError,
} from "../post-hearing-task.model";
import { PostHearingTaskService } from "../post-hearing-task.service";
import { PostHearingActionsSectionComponent } from "./post-hearing-actions-section.component";
import {
  PostHearingActionRow,
  PostHearingTabId,
  isBlockingPostHearingAction,
  isResolvedPostHearingAction,
} from "./post-hearing-workspace.models";

const HEARING_OUTPUT_CATEGORIES = new Set(["hearing-result", "post-hearing-paperwork"]);

@Component({
  selector: "phms-post-hearing-workspace",
  standalone: true,
  imports: [HearingPhaseBarDirective,
    CommonModule, EzButtonComponent,
    YourDecisionComponent,
    LegalReviewWorkspacePanelComponent,
    PostHearingActionsSectionComponent,
    PhmsCaseDocumentsComponent,
    PhmsAuditActivityLogComponent,
    PhmsCaseLifecycleComponent,
  ],
  templateUrl: "./post-hearing-workspace.component.html",
  styleUrl: "./post-hearing-workspace.component.scss",
})
export class PostHearingWorkspaceComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cycleContext = inject(HearingCycleContextStore, { optional: true });
  private readonly router = inject(Router);
  readonly navigation = inject(HearingNavigationService);
  readonly usesRail = computed(() => this.navigation.context()?.phase === "post");

  constructor() {
    effect(() => {
      if (!this.usesRail()) return;
      if (this.navigation.context()?.queryParams["viewDecision"] === "true") this.decisionReferenceOpen.set(true);
      const section = this.navigation.section();
      if (section === "actions" || section === "closeout" || section === "decision" || section === "documents" || section === "activity") {
        this.activeTab.set(section);
      }
    });
  }
  private readonly roleService = inject(RoleService);
  private readonly recordService = inject(HearingRecordService);
  private readonly milestoneService = inject(HearingMilestoneOverviewService);
  private readonly taskService = inject(PostHearingTaskService);
  private readonly legalReviewService = inject(LegalReviewService);
  private readonly documentService = inject(PhmsDocumentService);

  @ViewChild(YourDecisionComponent) private decisionView?: YourDecisionComponent;

  @Input({ required: true })
  set scheduledProceedingId(value: number | null) {
    if (value !== this._proceedingId()) { this.currentLegalStatus.set(undefined); this.decisionReferenceOpen.set(false); }
    this._proceedingId.set(value);
    if (value && value > 0) {
      this.load(value);
    }
  }
  get scheduledProceedingId(): number | null { return this._proceedingId(); }
  get scheduledProceedingIdForReview(): number { return this._proceedingId() ?? 0; }

  /**
   * Legal review routed in from the Legal Review dashboard (the `legalReview` query param).
   * Arriving with a review opens the Legal Review tab directly.
   */
  @Input()
  set legalReviewId(value: number | null) {
    this._legalReviewId.set(value);
    if (value !== null && value > 0 && !this.usesRail()) {
      this.selectTab("legal");
    }
  }
  get legalReviewId(): number | null { return this._legalReviewId(); }

  private readonly _proceedingId = signal<number | null>(null);
  private readonly _legalReviewId = signal<number | null>(null);
  readonly activeTab = signal<PostHearingTabId>("actions");
  readonly activeTaskLabel = computed(() => ({
    actions: "Actions & closeout", closeout: "Actions & closeout", decision: "Decision & Review",
    legal: "Decision & Review", documents: "Documents", activity: "Activity",
  })[this.activeTab()]);
  readonly isLoading = signal(false);
  readonly isActing = signal(false);
  readonly lifecycleBusy = signal(false);
  readonly legalBusy = signal(false);
  readonly currentLegalStatus = signal<string | undefined>(undefined);
  readonly amendmentBusy = signal(false);
  readonly decisionReferenceOpen = signal(false);
  private loadRevision = 0;
  private readonly completionSource = signal<PostHearingCompletionSource | null>(null);
  readonly canTransition = this.roleService.hasAnyPermissionSignal("workflow_runtime.transition");
  readonly loadError = signal<string | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly record = signal<HearingRecordDto | null>(null);
  readonly overview = signal<HearingMilestoneOverview | null>(null);
  readonly tasks = signal<PostHearingTask[]>([]);
  readonly legalReviews = signal<WorkflowDecisionRecord[]>([]);
  readonly pendingApprovals = signal<WorkflowDecisionRecord[]>([]);
  readonly documents = signal<PhmsDocument[]>([]);
  readonly canReviewGrant = this.roleService.hasAnyRoleSignal("legal");
  readonly canApprovePostHearing = this.roleService.hasAnyRoleSignal("operations_manager");

  readonly incarceratedPersonId = computed(() => this.record()?.incarceratedPersonId ?? null);
  readonly subjectName = computed(() => {
    const demographics = this.record()?.context?.demographics;
    return demographics?.fullName?.trim()
      || [demographics?.firstName, demographics?.lastName].map(part => part?.trim()).filter(Boolean).join(" ");
  });
  readonly workflowInstanceId = computed(() => this.overview()?.workflowInstanceId ?? this.primaryTask()?.workflowInstanceId ?? null);
  readonly primaryTask = computed(() => {
    const workflowId = this.overview()?.workflowInstanceId;
    const personId = this.incarceratedPersonId();
    return this.tasks().find((task) => workflowId && task.workflowInstanceId === workflowId)
      ?? this.tasks().find((task) => personId && task.subjectId === personId)
      ?? null;
  });
  readonly legalReview = computed(() => this.legalReviews().find((item) => item.workflowInstanceId === this.workflowInstanceId()) ?? null);
  /** Review shown on the Legal Review tab: the routed-in review first, otherwise the current review for this hearing. */
  readonly activeLegalReviewId = computed(() => this._legalReviewId() ?? this.legalReview()?.id ?? null);
  readonly completionApproval = computed(() => this.pendingApprovals().find((item) => item.workflowInstanceId === this.workflowInstanceId()) ?? null);
  readonly decisionOutcome = computed(() => this.record()?.decision?.outcome ?? null);
  readonly postMilestone = computed(() => this.overview()?.milestones.find((item) => normalize(item.milestoneCode) === "post_hearing") ?? null);
  readonly postStep = computed(() => this.postMilestone()?.steps[0] ?? null);
  readonly hearingOutputs = computed(() => this.documents().filter((document) => HEARING_OUTPUT_CATEGORIES.has(document.documentCategoryCode)));

  readonly actions = computed<PostHearingActionRow[]>(() => buildActions({
    record: this.record(),
    postStep: this.postStep(),
    task: this.primaryTask(),
    legalReview: this.legalReview(),
    currentLegalStatus: this.currentLegalStatus(),
    completionApproval: this.completionApproval(),
    hearingOutputs: this.hearingOutputs(),
    canCompleteReview: this.canCompleteReview(),
    canReviewGrant: this.canReviewGrant(),
    canApprovePostHearing: this.canApprovePostHearing() && this.hasCurrentCompletionSource(),
  }));
  readonly resolvedActionCount = computed(() => this.actions().filter(isResolvedPostHearingAction).length);
  readonly blockers = computed(() => this.actions().filter(isBlockingPostHearingAction));
  readonly isCloseoutReady = computed(() => !this.isLoading() && !this.loadError() && this.actions().length > 0 && this.blockers().length === 0);
  readonly progressSegments = [0, 1, 2, 3, 4];
  readonly reviewStatusLabel = computed(() => {
    if (this.usesRail()) return formatWorkflowStatus(this.currentLegalStatus() ?? this.legalReview()?.statusCode ?? "not_started");
    const closeout = this.navigation.closeoutEvidence();
    if (closeout?.proceedingId === this.scheduledProceedingId && closeout.closed) return "Package frozen";
    return ["complete", "completed"].includes(normalize(this.postMilestone()?.stateCode)) ? "Review complete" : this.postMilestone()?.stateLabel || "Not started";
  });

  selectTab(tab: PostHearingTabId): void {
    this.activeTab.set(tab);
    if (this.usesRail()) this.navigation.selectSection(tab === "legal" ? "decision" : tab);
  }

  onTaskPickerChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    if (value === "actions" || value === "decision" || value === "legal" || value === "documents" || value === "activity") {
      this.selectTab(value);
    }
  }

  refresh(): void {
    if (this.isLoading() || this.isActing() || this.lifecycleBusy() || this.legalBusy() || this.amendmentBusy()) return;
    const proceedingId = this._proceedingId();
    if (proceedingId) {
      this.load(proceedingId);
    }
  }

  onClosedStateChanged(closed: boolean | null): void {
    if (this.scheduledProceedingId) this.navigation.closeoutEvidence.set({ proceedingId: this.scheduledProceedingId, closed });
  }

  onActionSelected(action: PostHearingActionRow): void {
    if (this.isLoading() || this.loadError() || this.isActing() || this.lifecycleBusy() || this.legalBusy() || this.amendmentBusy()) return;
    if (action.id === "legal" && this.legalReview() && this.canReviewGrant()) {
      const reviewId = this.legalReview()!.id;
      this._legalReviewId.set(reviewId);
      this.activeTab.set("legal");
      void this.router.navigate([], {
        queryParams: { legalReview: reviewId, ...(this.usesRail() ? { section: "decision" } : {}) },
        queryParamsHandling: "merge",
        state: { phmsReturn: { url: "/phms/dashboard?view=legal", label: "Operations dashboard" } },
      });
      return;
    }
    if (action.id === "review" && this.canCompleteReview()) {
      const workflowInstanceId = this.workflowInstanceId()!;
      const stepCode = this.postStep()?.stepCode ?? this.primaryTask()?.currentStepCode;
      this.runAction(this.taskService.completeStep(workflowInstanceId, stepCode!, this.completionSource()!.eTag));
      return;
    }
    if (action.id === "approval" && this.completionApproval() && this.canApprovePostHearing() && this.hasCurrentCompletionSource()) {
      this.runAction(this.taskService.approvePostHearingCompletion(this.completionApproval()!.id, this.completionSource()!.eTag));
      return;
    }
    this.selectTab(action.targetTab);
  }

  onDecisionReferenceClick(event: Event): void {
    if (this.amendmentBusy()) event.preventDefault();
  }

  onDecisionAmended(): void {
    this.currentLegalStatus.set(undefined);
    this.legalBusy.set(false); this.amendmentBusy.set(false); this._legalReviewId.set(null);
    this.navigation.legalReviewEvidence.set(null);
    void this.router.navigate([], { queryParams: { legalReview: null }, queryParamsHandling: "merge", replaceUrl: true });
    const id = this._proceedingId(); if (id) this.load(id);
  }
  onLegalReviewInvalidated(): void { this.currentLegalStatus.set("unknown"); this.navigation.legalReviewEvidence.set(null); }
  onLegalReviewLoaded(detail?: LegalReviewDetail): void {
    if (detail?.isCurrentDecision) this.currentLegalStatus.set(detail.review.statusCode);
    if (this.usesRail() && detail) this.navigation.legalReviewEvidence.set(detail.isCurrentDecision ? { proceedingId: this.scheduledProceedingIdForReview, statusCode: detail.review.statusCode } : null);
    this.decisionView?.refreshDocuments();
  }

  private canCompleteReview(): boolean {
    const task = this.primaryTask();
    const workflowInstanceId = this.workflowInstanceId();
    const workflowStatus = normalize(this.overview()?.workflowStatusCode ?? task?.workflowStatusCode);
    const stepCode = this.postStep()?.stepCode ?? task?.currentStepCode;
    const stepStatus = normalize(this.postStep()?.stateCode ?? task?.currentStepStatusCode);

    // The milestone overview is case-specific and comes from the workflow step instances. Do not
    // require the same case to also appear in the paged, queue-filtered global worklist: a missing
    // queue assignment, pagination, or a transient worklist read used to make an otherwise active
    // review impossible to complete. The runtime transition remains the authority and enforces all
    // required decision/approval gates on the server.
    return Boolean(
      this.canTransition() && !this.isLoading() && !this.loadError() && workflowInstanceId
      && typeof stepCode === "string"
      && stepCode.trim().length > 0
      && workflowStatus !== "completed"
      && stepStatus === "in_progress"
      && this.completionSource()?.workflowInstanceId === workflowInstanceId
      && this.completionSource()?.stepCode === stepCode
      && (normalize(this.decisionOutcome()?.finding) !== "suitable" || (this.currentLegalStatus() ?? this.legalReview()?.statusCode) === "approved")
      && !this.completionApproval()
    );
  }

  private hasCurrentCompletionSource(): boolean {
    const source = this.completionSource();
    return this.canTransition() && !!source && source.workflowInstanceId === this.workflowInstanceId()
      && source.stepCode === (this.postStep()?.stepCode ?? this.primaryTask()?.currentStepCode);
  }

  private load(proceedingId: number): void {
    const revision = ++this.loadRevision;
    this.completionSource.set(null);
    this.isLoading.set(true);
    this.loadError.set(null);
    this.recordService.getRecord(proceedingId)
      .pipe(
        switchMap((record) => forkJoin({
          record: of(record),
          overview: record.incarceratedPersonId
            ? this.milestoneService.getMilestoneOverview(record.incarceratedPersonId)
            : of(null),
          tasks: this.usesRail() ? of({ items: [] }) : this.taskService.getTasks().pipe(catchError(() => of({ totalCount: 0, items: [] }))),
          legalReviews: this.legalReviewService.getDashboard(),
          approvals: this.usesRail() ? of({ items: [] }) : this.taskService.getPendingPostHearingApprovals(),
          documents: this.usesRail() ? of([]) : this.documentService.getForHearing(proceedingId),
        })),
        switchMap((result) => {
          const task = result.tasks.items.find(item => item.workflowInstanceId === result.overview?.workflowInstanceId)
            ?? result.tasks.items.find(item => item.subjectId === result.record.incarceratedPersonId);
          const workflowId = result.overview?.workflowInstanceId ?? task?.workflowInstanceId;
          const postStep = result.overview?.milestones.find(item => normalize(item.milestoneCode) === "post_hearing")?.steps[0];
          const stepCode = postStep?.stepCode ?? task?.currentStepCode;
          const active = normalize(postStep?.stateCode ?? task?.currentStepStatusCode) === "in_progress";
          if (!this.canTransition() || !active || !workflowId || !stepCode) return of({ ...result, completionSource: null });
          return this.taskService.getCompletionSource(workflowId, stepCode).pipe(map(source => {
            if (source.workflowInstanceId !== workflowId || source.stepCode !== stepCode || !Number.isSafeInteger(source.workflowStepInstanceId) || (source.workflowStepInstanceId ?? 0) < 1 || !source.eTag?.trim())
              throw new Error("The post-hearing review source is unavailable. Reload before completing it.");
            return { ...result, completionSource: source };
          }));
        }),
        catchError((error: unknown) => {
          if (revision === this.loadRevision) this.loadError.set(mapPostHearingError(error).messages[0] ?? "The Post-Hearing workspace could not be loaded.");
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        if (revision !== this.loadRevision) return;
        if (result) {
          this.record.set(result.record);
          this.completionSource.set(result.completionSource);
          if (result.record) this.cycleContext?.record.set(result.record);
          this.overview.set(result.overview);
          this.cycleContext?.milestoneOverview.set(result.overview);
          this.tasks.set(result.tasks.items ?? []);
          this.legalReviews.set(result.legalReviews.items.filter(item => item.scheduledProceedingId === proceedingId).map(item => ({
            id: item.decisionRecordId, workflowInstanceId: item.workflowInstanceId, statusCode: item.statusCode,
            decisionRecordTypeCode: "review", createdUtc: item.grantRecordedUtc,
          })));
          this.currentLegalStatus.set(undefined);
          this.pendingApprovals.set(result.approvals.items ?? []);
          this.documents.set(result.documents);
        }
        this.isLoading.set(false);
      });
  }

  private runAction(action$: ReturnType<PostHearingTaskService["completeStep"]>): void {
    if (this.isActing()) {
      return;
    }
    this.isActing.set(true);
    this.actionError.set(null);
    action$.pipe(
      map(() => true),
      catchError((error: unknown) => {
        const failure = error as { status?: number; error?: { problemCode?: string } };
        if (failure?.status === 412 || (failure?.status === 409 && failure.error?.problemCode === "workflow.concurrency.conflict"))
          this.completionSource.set(null);
        this.actionError.set(mapPostHearingError(error).messages[0] ?? "The action could not be completed.");
        return of(false);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((succeeded) => {
      this.isActing.set(false);
      if (succeeded) {
        this.refresh();
      }
    });
  }
}

interface ActionInputs {
  record: HearingRecordDto | null;
  postStep: HearingMilestoneOverview["milestones"][number]["steps"][number] | null;
  task: PostHearingTask | null;
  legalReview: WorkflowDecisionRecord | null;
  currentLegalStatus?: string;
  completionApproval: WorkflowDecisionRecord | null;
  hearingOutputs: readonly PhmsDocument[];
  canCompleteReview: boolean;
  canReviewGrant: boolean;
  canApprovePostHearing: boolean;
}

function buildActions(input: ActionInputs): PostHearingActionRow[] {
  const outcome = input.record?.decision?.outcome ?? null;
  const hasDecision = outcome !== null;
  const isGrant = normalize(outcome?.finding) === "suitable";
  const legalStatus = input.currentLegalStatus ?? input.legalReview?.statusCode;
  const legalBlocked = legalStatus !== "approved";
  const postState = normalize(input.postStep?.stateCode);
  const postComplete = postState === "complete" || postState === "completed";

  return [
    {
      id: "decision", label: "Final decision record", description: "Confirm the panel finding and final rationale.",
      statusCode: hasDecision ? "complete" : "blocked", statusLabel: hasDecision ? "Complete" : "Blocked",
      owner: "Hearing Panel", updatedUtc: outcome?.recordedUtc, nextStep: hasDecision ? "Decision is final" : "Record the decision",
      actionLabel: "View", targetTab: "decision",
    },
    {
      id: "legal", label: "Legal review", description: "Resolve required review of a grant decision.",
      statusCode: !hasDecision ? "blocked" : !isGrant ? "not_required" : legalBlocked ? "blocked" : "complete",
      statusLabel: !hasDecision ? "Blocked" : !isGrant ? "Not required" : legalStatus === "returned" ? "Returned" : legalBlocked ? (legalStatus === "pending" ? "Pending" : "Not confirmed") : "Cleared",
      owner: "Legal", updatedUtc: input.legalReview?.createdUtc ?? null,
      nextStep: !hasDecision ? "Waiting on decision" : !isGrant ? "Denial requires no Legal review" : legalStatus === "returned" ? "Amend the decision and obtain Legal approval" : legalBlocked ? "Legal approval required" : "Current decision approved",
      actionLabel: input.legalReview && input.canReviewGrant ? "Open" : "View", targetTab: "legal",
    },
    {
      id: "documents", label: "Decision notices and documents", description: "Confirm hearing outputs are generated and associated.",
      statusCode: input.hearingOutputs.length > 0 ? "complete" : "not_started",
      statusLabel: input.hearingOutputs.length > 0 ? "Available" : "Not started", owner: "Post Hearing",
      updatedUtc: input.hearingOutputs[0]?.linkedUtc ?? null,
      nextStep: input.hearingOutputs.length > 0 ? `${input.hearingOutputs.length} output(s) available` : "Generate or associate outputs",
      actionLabel: "Open", targetTab: "documents",
    },
    {
      id: "review", label: "Post-hearing review", description: "Complete the active workflow review step.",
      statusCode: postComplete ? "complete" : postState === "blocked" ? "blocked" : postState === "in_progress" ? "in_progress" : "not_started",
      statusLabel: input.postStep?.stateLabel || formatWorkflowStatus(input.task?.currentStepStatusCode),
      owner: input.task?.currentAssigneeDisplayName || input.task?.currentQueueDisplayText || "Post Hearing",
      updatedUtc: input.task?.statusChangedUtc ?? input.postStep?.completedUtc ?? input.postStep?.startedUtc,
      nextStep: postComplete ? "Review complete" : input.canCompleteReview ? "Ready to complete" : "Resolve prerequisite actions",
      actionLabel: input.canCompleteReview ? "Complete" : "Review", targetTab: "closeout",
    },
    {
      id: "approval", label: "Operations approval", description: "Obtain authorization for post-hearing completion.",
      statusCode: !hasDecision ? "blocked" : input.completionApproval ? "blocked" : "complete",
      statusLabel: !hasDecision ? "Blocked" : input.completionApproval ? "Pending" : "Cleared", owner: "Operations Manager",
      updatedUtc: input.completionApproval?.createdUtc ?? null,
      nextStep: !hasDecision ? "Waiting on decision" : input.completionApproval ? "Operations approval required" : "No pending approval",
      actionLabel: input.completionApproval && input.canApprovePostHearing ? "Approve" : "View", targetTab: "closeout",
    },
  ];
}

function normalize(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}
